
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { useLanguage } from '@/contexts/LanguageContext';
import { MessageSquare, Wifi, WifiOff } from 'lucide-react';
import RatingSelector from './RatingSelector';
import { useIsMobile } from '@/hooks/use-mobile';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

// Define form schema
const feedbackSchema = z.object({
  rating: z.number().min(1, { 
    message: "Please provide a rating" 
  }).max(5),
  feedback: z.string().min(5, { 
    message: "Feedback must be at least 5 characters" 
  }).max(500, { 
    message: "Feedback must be less than 500 characters" 
  }),
});

type FeedbackFormValues = z.infer<typeof feedbackSchema>;

const FeedbackDialog: React.FC = () => {
  const { language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  
  const form = useForm<FeedbackFormValues>({
    resolver: zodResolver(feedbackSchema),
    defaultValues: {
      rating: 0,
      feedback: '',
    },
  });

  // Update online status
  React.useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Save feedback to IndexedDB
  const saveFeedbackToIndexedDB = (feedbackData: any) => {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }
      
      const request = indexedDB.open('adaaltFeedback', 1);
      
      request.onsuccess = (event) => {
        // Fix: Use as IDBOpenDBRequest to safely access result
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(['feedback'], 'readwrite');
        const store = transaction.objectStore('feedback');
        
        const addRequest = store.add(feedbackData);
        
        addRequest.onsuccess = () => {
          console.log('Feedback saved to IndexedDB');
          resolve(true);
        };
        
        addRequest.onerror = (error) => {
          console.error('Error saving feedback to IndexedDB:', error);
          reject(error);
        };
      };
      
      request.onerror = (error) => {
        reject(error);
      };
    });
  };

  // Save offline feedback for syncing later
  const saveOfflineFeedback = (feedbackData: any) => {
    return new Promise((resolve, reject) => {
      if (!('indexedDB' in window)) {
        reject(new Error('IndexedDB not supported'));
        return;
      }
      
      const request = indexedDB.open('adaaltFeedback', 1);
      
      request.onsuccess = (event) => {
        // Fix: Use as IDBOpenDBRequest to safely access result
        const db = (event.target as IDBOpenDBRequest).result;
        const transaction = db.transaction(['pendingFeedback'], 'readwrite');
        const store = transaction.objectStore('pendingFeedback');
        
        const addRequest = store.add(feedbackData);
        
        addRequest.onsuccess = () => {
          console.log('Offline feedback saved for future sync');
          resolve(true);
        };
        
        addRequest.onerror = (error) => {
          console.error('Error saving offline feedback:', error);
          reject(error);
        };
      };
      
      request.onerror = (error) => {
        reject(error);
      };
    });
  };

  const onSubmit = async (data: FeedbackFormValues) => {
    setIsSubmitting(true);
    
    try {
      const feedbackData = {
        id: Date.now().toString(),
        rating: data.rating,
        feedback: data.feedback,
        user_id: user?.id || 'anonymous',
        created_at: new Date().toISOString(),
      };

      // Try to save to Supabase if online
      if (navigator.onLine) {
        try {
          // Save to Supabase
          const { error } = await supabase.from('feedback').insert({
            rating: feedbackData.rating,
            feedback: feedbackData.feedback,
            user_id: feedbackData.user_id
          });
          
          if (error) throw error;
        } catch (supabaseError) {
          console.error('Error saving to Supabase:', supabaseError);
          // Continue as we'll save locally
        }
      }

      // Try to save to IndexedDB for persistence
      await saveFeedbackToIndexedDB(feedbackData);
      
      // Save to localStorage
      const existingFeedback = JSON.parse(localStorage.getItem('adaaltFeedback') || '[]');
      existingFeedback.push(feedbackData);
      localStorage.setItem('adaaltFeedback', JSON.stringify(existingFeedback));
      
      if (navigator.onLine) {
        toast.success(language === 'ur' ? 'آپ کی رائے کامیابی سے جمع کر دی گئی' : 'Your feedback has been submitted successfully');
      } else {
        // We're offline, save for later sync
        await saveOfflineFeedback(feedbackData);
        toast.success(
          language === 'ur' 
            ? 'آپ کی رائے محفوظ کر لی گئی ہے اور آن لائن ہونے پر سنک کی جائے گی' 
            : 'Your feedback has been saved and will be synced when you are online'
        );
      }
      
      // Dispatch custom event to notify FeedbackStats component
      window.dispatchEvent(new Event('feedbackUpdated'));
      
      form.reset();
      setIsOpen(false);
    } catch (error) {
      console.error('Error saving feedback:', error);
      toast.error(language === 'ur' ? 'آپ کی رائے جمع کرنے میں مسئلہ پیش آیا' : 'There was an issue submitting your feedback');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset form when dialog is closed
      form.reset();
    }
  };

  const renderFeedbackForm = () => (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="text-center mb-4">
          <FormLabel className="block mb-2">
            {language === 'ur' ? 'ہماری خدمت کو درجہ دیں' : 'Rate our service'}
          </FormLabel>
          <FormField
            control={form.control}
            name="rating"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <RatingSelector 
                    rating={field.value} 
                    onRatingChange={(val) => field.onChange(val)} 
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="feedback"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {language === 'ur' ? 'آپ کی رائے' : 'Your feedback'}
              </FormLabel>
              <FormControl>
                <Textarea
                  placeholder={language === 'ur' ? 'اپنی رائے یہاں لکھیں...' : 'Write your feedback here...'}
                  className={`min-h-[100px] ${isMobile ? 'text-base' : ''}`}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex items-center justify-between">
          <div className="flex items-center text-xs">
            {isOnline ? (
              <div className="flex items-center text-green-600">
                <Wifi size={14} className="mr-1" />
                {language === 'ur' ? 'آن لائن' : 'Online'}
              </div>
            ) : (
              <div className="flex items-center text-orange-500">
                <WifiOff size={14} className="mr-1" />
                {language === 'ur' ? 'آف لائن' : 'Offline'}
              </div>
            )}
          </div>
        </div>

        <div className={`flex gap-2 ${language === 'ur' ? 'flex-row-reverse' : ''}`}>
          <Button 
            type="submit" 
            className={`adalat-button text-white ${isMobile ? 'w-full mobile-button' : ''}`}
            disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-b-transparent"></div>
                {language === 'ur' ? 'جمع کیا جا رہا ہے...' : 'Submitting...'}
              </>
            ) : (
              language === 'ur' ? 'جمع کریں' : 'Submit'
            )}
          </Button>
          <Button 
            type="button" 
            variant="outline" 
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
            className={isMobile ? 'w-full mobile-button' : ''}>
            {language === 'ur' ? 'منسوخ کریں' : 'Cancel'}
          </Button>
        </div>
      </form>
    </Form>
  );

  const handleButtonClick = () => {
    setIsOpen(true);
  };

  if (isMobile) {
    return (
      <>
        <Button 
          onClick={handleButtonClick}
          className={`adalat-button text-white py-2 px-3 flex items-center gap-1.5 ${isMobile ? 'mobile-button' : ''}`} 
          size="sm">
          <MessageSquare size={isMobile ? 18 : 16} />
          {language === 'ur' ? 'رائے دیں' : 'Feedback'}
        </Button>

        <Sheet open={isOpen} onOpenChange={handleOpenChange}>
          <SheetContent 
            side="bottom" 
            className={`${language === 'ur' ? 'rtl' : 'ltr'} rounded-t-xl ios-fixed-fix`}
          >
            <SheetHeader className="mb-4">
              <SheetTitle className="flex items-center gap-2">
                <MessageSquare size={18} />
                {language === 'ur' ? 'آپ کی رائے' : 'Your Feedback'}
              </SheetTitle>
            </SheetHeader>
            {renderFeedbackForm()}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <>
      <Button 
        onClick={handleButtonClick}
        className={`adalat-button text-white py-2 px-3 flex items-center gap-1.5 ${isMobile ? 'mobile-button' : ''}`} 
        size="sm">
        <MessageSquare size={isMobile ? 18 : 16} />
        {language === 'ur' ? 'رائے دیں' : 'Feedback'}
      </Button>

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className={`max-w-md ${language === 'ur' ? 'rtl' : 'ltr'}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare size={18} />
              {language === 'ur' ? 'آپ کی رائے' : 'Your Feedback'}
            </DialogTitle>
            <DialogDescription>
              {language === 'ur' 
                ? 'اپنی قیمتی رائے سے ہمیں بہتر بننے میں مدد کریں'
                : 'Help us improve with your valuable feedback'}
            </DialogDescription>
          </DialogHeader>

          {renderFeedbackForm()}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeedbackDialog;
